import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";

interface SalesReport {
  id: string;
  period: string;
  totalSales: number;
  totalOrders: number;
  avgOrderValue: number;
  topProduct: string;
  growth: number;
}

const MOCK_REPORTS: SalesReport[] = [
  { id: "1", period: "2024-01-15", totalSales: 1250.99, totalOrders: 15, avgOrderValue: 83.40, topProduct: "Premium Widget", growth: 12.5 },
  { id: "2", period: "2024-01-14", totalSales: 980.50, totalOrders: 12, avgOrderValue: 81.71, topProduct: "Basic Widget", growth: -5.2 },
  { id: "3", period: "2024-01-13", totalSales: 1450.00, totalOrders: 18, avgOrderValue: 80.56, topProduct: "Premium Widget", growth: 8.3 },
  { id: "4", period: "2024-01-12", totalSales: 890.75, totalOrders: 10, avgOrderValue: 89.08, topProduct: "Deluxe Widget", growth: -2.1 },
  { id: "5", period: "2024-01-11", totalSales: 1100.25, totalOrders: 14, avgOrderValue: 78.59, topProduct: "Premium Widget", growth: 15.7 },
  { id: "6", period: "2024-01-10", totalSales: 1320.00, totalOrders: 16, avgOrderValue: 82.50, topProduct: "Basic Widget", growth: 3.4 },
];

export default function ReportsSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const [dateRange, setDateRange] = useState<string>("week");

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    inputBg: isDarkMode ? "bg-dark-surface" : "bg-white",
    tableRowHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-50",
  };

  const totalSales = MOCK_REPORTS.reduce((acc, r) => acc + r.totalSales, 0);
  const totalOrders = MOCK_REPORTS.reduce((acc, r) => acc + r.totalOrders, 0);
  const avgOrderValue = totalSales / totalOrders;
  const avgGrowth = MOCK_REPORTS.reduce((acc, r) => acc + r.growth, 0) / MOCK_REPORTS.length;

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${themeClasses.text}`}>{t("posMenu.salesReports")}</h1>
          <p className={themeClasses.textSecondary}>{t("reports.subtitle")}</p>
        </div>
        <div className="flex gap-2">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text}`}
          >
            <option value="today">{t("reports.today")}</option>
            <option value="week">{t("reports.thisWeek")}</option>
            <option value="month">{t("reports.thisMonth")}</option>
            <option value="quarter">{t("reports.thisQuarter")}</option>
            <option value="year">{t("reports.thisYear")}</option>
          </select>
          <button className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text} font-medium flex items-center gap-2`}>
            <i className="ri-download-2-line"></i>
            {t("posOrders.export")}
          </button>
          <button className={`px-4 py-2 rounded-xl border ${themeClasses.border} ${themeClasses.text} font-medium flex items-center gap-2`}>
            <i className="ri-printer-line"></i>
            {t("posOrders.print")}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("reports.totalSales")}</span>
            <i className="ri-money-dollar-circle-line text-emerald-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold text-emerald-500`}>${totalSales.toFixed(2)}</p>
          <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>{t("reports.periodLabel")}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("reports.totalOrders")}</span>
            <i className="ri-shopping-bag-line text-blue-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>{totalOrders}</p>
          <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>{t("reports.periodLabel")}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("reports.avgOrderValue")}</span>
            <i className="ri-line-chart-line text-violet-500 text-xl"></i>
          </div>
          <p className={`text-2xl font-bold ${themeClasses.text}`}>${avgOrderValue.toFixed(2)}</p>
          <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>{t("reports.periodLabel")}</p>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-4`}>
          <div className="flex items-center justify-between mb-2">
            <span className={themeClasses.textSecondary}>{t("reports.avgGrowth")}</span>
            <i className={`ri-arrow-${avgGrowth >= 0 ? "up" : "down"}-line ${avgGrowth >= 0 ? "text-emerald-500" : "text-red-500"} text-xl`}></i>
          </div>
          <p className={`text-2xl font-bold ${avgGrowth >= 0 ? "text-emerald-500" : "text-red-500"}`}>
            {avgGrowth >= 0 ? "+" : ""}{avgGrowth.toFixed(1)}%
          </p>
          <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>{t("reports.comparedToPrevious")}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-6`}>
          <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>{t("reports.salesChart")}</h3>
          <div className="h-64 flex items-center justify-center">
            <div className="flex items-end gap-2 h-48">
              {MOCK_REPORTS.map((report) => (
                <div key={report.id} className="flex flex-col items-center">
                  <div
                    className="w-12 bg-primary-500 rounded-t-lg transition-all hover:bg-[#5ed83a]"
                    style={{ height: `${(report.totalSales / 1500) * 100}%` }}
                  ></div>
                  <span className={`text-xs ${themeClasses.textSecondary} mt-2`}>
                    {report.period.split("-")[2]}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-6`}>
          <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>{t("reports.topProducts")}</h3>
          <div className="space-y-4">
            {[
              { name: "Premium Widget", sales: 45, revenue: 2250 },
              { name: "Basic Widget", sales: 32, revenue: 1280 },
              { name: "Deluxe Widget", sales: 18, revenue: 1440 },
              { name: "Standard Widget", sales: 12, revenue: 600 },
            ].map((product, index) => (
              <div key={product.name} className="flex items-center gap-4">
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                  index === 0 ? "bg-primary-500 text-gray-900" :
                  index === 1 ? "bg-blue-500 text-white" :
                  index === 2 ? "bg-violet-500 text-white" :
                  "bg-gray-500 text-white"
                }`}>
                  {index + 1}
                </span>
                <div className="flex-1">
                  <p className={`font-medium ${themeClasses.text}`}>{product.name}</p>
                  <p className={`text-xs ${themeClasses.textSecondary}`}>{product.sales} {t("reports.unitsSold")}</p>
                </div>
                <p className={`font-bold ${themeClasses.text}`}>${product.revenue}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} overflow-hidden`}>
        <div className={`p-4 border-b ${themeClasses.border}`}>
          <h3 className={`text-lg font-semibold ${themeClasses.text}`}>{t("reports.dailyBreakdown")}</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`${themeClasses.cardBg} border-b ${themeClasses.border}`}>
              <tr>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("reports.date")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("reports.sales")}</th>
                <th className={`px-4 py-3 text-center text-sm font-medium ${themeClasses.textSecondary}`}>{t("reports.orders")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("reports.avgValue")}</th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${themeClasses.textSecondary}`}>{t("reports.topProduct")}</th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${themeClasses.textSecondary}`}>{t("reports.growth")}</th>
              </tr>
            </thead>
            <tbody>
              {MOCK_REPORTS.map((report) => (
                <tr key={report.id} className={`border-b ${themeClasses.border} ${themeClasses.tableRowHover}`}>
                  <td className={`px-4 py-3 font-medium ${themeClasses.text}`}>{report.period}</td>
                  <td className={`px-4 py-3 text-right font-bold text-emerald-500`}>${report.totalSales.toFixed(2)}</td>
                  <td className={`px-4 py-3 text-center ${themeClasses.text}`}>{report.totalOrders}</td>
                  <td className={`px-4 py-3 text-right ${themeClasses.text}`}>${report.avgOrderValue.toFixed(2)}</td>
                  <td className={`px-4 py-3 ${themeClasses.textSecondary}`}>{report.topProduct}</td>
                  <td className={`px-4 py-3 text-right font-medium ${report.growth >= 0 ? "text-emerald-500" : "text-red-500"}`}>
                    {report.growth >= 0 ? "+" : ""}{report.growth}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
